import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CurrencySettings = () => {
  const [formData, setFormData] = useState({
    company_currency: 'NOK',
    currency_api_enabled: 'true', // Default to true
    currency_api_url: 'https://open.er-api.com/v6/latest/NOK',
    currency_last_update: ''
  });

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Fetch currency settings and rates
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Get currency settings
        const settingsRes = await axios.get('/api/settings/currencies', config);
        
        // Update form data with settings
        const newFormData = { ...formData };
        Object.keys(settingsRes.data).forEach(key => {
          if (newFormData[key] !== undefined) {
            newFormData[key] = settingsRes.data[key];
          }
        });
        
        setFormData(newFormData);
        
        // Get currency rates
        const ratesRes = await axios.get('/api/settings/currencies/rates', config);
        setRates(ratesRes.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching currency settings:', err);
        setError('Failed to load currency settings');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  // Handle input changes for settings
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'true' : 'false') : value
    }));
  };

  // Handle rate change
  const handleRateChange = (currency, newRate) => {
    const updatedRates = rates.map(rate => {
      if (rate.from_currency === currency) {
        return { ...rate, rate: newRate };
      }
      return rate;
    });
    setRates(updatedRates);
  };

  // Refresh rates from API
  const refreshRates = async () => {
    try {
      setRefreshing(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.post('/api/settings/currencies/refresh', {}, config);
      
      // Update rates
      setRates(res.data.rates);
      
      // Update last update time using current timestamp
      const nowIsoString = new Date().toISOString();
      
      setFormData(prev => ({
        ...prev,
        currency_last_update: nowIsoString
      }));
      
      // Also update the company setting
      await axios.post('/api/settings/currencies', {
        settings: {
          currency_last_update: nowIsoString
        }
      }, config);
      
      setSuccess('Exchange rates refreshed successfully');
    } catch (err) {
      console.error('Error refreshing rates:', err);
      setError('Failed to refresh exchange rates');
    } finally {
      setRefreshing(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Update settings and rates
      const data = {
        settings: formData,
        rates: rates
      };
      
      await axios.post('/api/settings/currencies', data, config);
      
      setSuccess('Currency settings updated successfully');
    } catch (err) {
      console.error('Error updating currency settings:', err);
      setError('Failed to update currency settings');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  if (loading && !formData.company_currency) {
    return <div className="text-center py-4">Loading currency settings...</div>;
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Currency Settings</h3>
      
      {/* Display error or success messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Currency Settings */}
        <div className="bg-white p-4 rounded-md border border-gray-200">
          <h4 className="text-md font-medium text-gray-800 mb-4">Company Currency</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="company_currency" className="block text-sm font-medium text-gray-700 mb-1">
                Default Company Currency
              </label>
              <select
                id="company_currency"
                name="company_currency"
                value={formData.company_currency}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="NOK">Norwegian Krone (NOK)</option>
                <option value="SEK">Swedish Krona (SEK)</option>
                <option value="EUR">Euro (EUR)</option>
                <option value="USD">US Dollar (USD)</option>
                <option value="CHF">Swiss Franc (CHF)</option>
                <option value="AUD">Australian Dollar (AUD)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                This is the main currency used for reporting and valuations.
              </p>
            </div>
            
            <div className="flex items-end">
              <div className="flex items-center h-10">
                <input
                  type="checkbox"
                  id="currency_api_enabled"
                  name="currency_api_enabled"
                  checked={true}
                  disabled={true}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-not-allowed opacity-60"
                />
                <label htmlFor="currency_api_enabled" className="ml-2 block text-sm text-gray-900">
                  Automatic currency rate updates (updated daily at midnight)
                </label>
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="currency_api_url" className="block text-sm font-medium text-gray-700 mb-1">
              Currency API URL
            </label>
            <input
              type="text"
              id="currency_api_url"
              name="currency_api_url"
              value={formData.currency_api_url}
              onChange={handleChange}
              readOnly={true}
              className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              We use the free Open Exchange Rates API. The base currency is set to your company currency.
            </p>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>Last update: <span className="font-medium">{formatDate(formData.currency_last_update)}</span></p>
          </div>
        </div>
        
        {/* Exchange Rates Table */}
        <div className="bg-white p-4 rounded-md border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium text-gray-800">Exchange Rates</h4>
            
            <button
              type="button"
              onClick={refreshRates}
              disabled={refreshing}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing...' : 'Refresh Rates'}
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exchange Rate to {formData.company_currency}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rates.map((rate) => (
                  <tr key={rate.from_currency}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {rate.from_currency}
                      </div>
                      {rate.from_currency === 'NOK' && <div className="text-xs text-gray-500">Norwegian Krone</div>}
                      {rate.from_currency === 'SEK' && <div className="text-xs text-gray-500">Swedish Krona</div>}
                      {rate.from_currency === 'EUR' && <div className="text-xs text-gray-500">Euro</div>}
                      {rate.from_currency === 'USD' && <div className="text-xs text-gray-500">US Dollar</div>}
                      {rate.from_currency === 'CHF' && <div className="text-xs text-gray-500">Swiss Franc</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        step="0.000001"
                        min="0.000001"
                        value={rate.rate}
                        onChange={(e) => handleRateChange(rate.from_currency, e.target.value)}
                        className="w-32 p-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={formData.currency_api_enabled === 'true' && rate.source === 'api'}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(rate.updated_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          rate.source === 'api' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {rate.source === 'api' ? 'API' : 'Manual'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 text-xs text-gray-500">
            <p>Exchange rates are used to convert deal values from their original currency to your company currency.</p>
            <p className="mt-1">Rates are automatically refreshed daily at midnight. You can also manually refresh rates using the "Refresh Rates" button.</p>
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CurrencySettings;